import React, { forwardRef } from 'react';
import ReactWaves_ from '@dschoon/react-waves';

interface ReactWavesProps {
  /** A URL to load. Omit when `mediaElt` is given — the two are alternatives. */
  audioFile?: string;
  /**
   * An existing media element to draw for, instead of letting WaveSurfer create one.
   *
   * The translated rows pass this so that ONE element both plays and is drawn. Letting WaveSurfer
   * make its own meant a second <audio> per row holding the same blob, and left the element that
   * actually sounds outside our control — it was created and destroyed by the library, on the
   * library's schedule, which is not the schedule the lecture runs on.
   */
  mediaElt?: HTMLMediaElement | null;
  className: string;
  options: any;
  /**
   * Pre-computed waveform peaks, forwarded to `wavesurfer.load(url, peaks)`.
   *
   * Required by the MediaElement backends, which never decode and would otherwise draw nothing.
   * Supplying ANY array — an empty one included — also suppresses the library's own fallback,
   * which reads the whole file back and runs decodeAudioData on it just to draw
   * (dist/index.es.js:4850). On a folder of voicings that fallback is the several seconds a clip
   * spends becoming playable, so it is never wanted here.
   */
  audioPeaks?: number[];
  volume: number;
  zoom: number;
  pos: number;
  playing: boolean;
  onLoading?: (data: any) => void;
  /**
   * Fires when WaveSurfer considers the track ready.
   *
   * This is how the instance is captured when peaks are supplied: `loading` is emitted by the
   * decode read that peaks exist to prevent, so with peaks it never fires at all.
   */
  onReady?: (data: any) => void;
  onPosChange?: (pos: number) => void;
  onFinish?: () => void;
  children?: React.ReactNode;
}

// Cast the imported component to a type that accepts refs.
const ReactWavesComponent = ReactWaves_ as unknown as React.ComponentType<
  ReactWavesProps & React.RefAttributes<any>
>;

export const ReactWaves = forwardRef<any, ReactWavesProps>((props, ref) => {
  return <ReactWavesComponent {...props} ref={ref} />;
});

ReactWaves.displayName = 'ReactWaves';
